import { Loader2 } from "lucide-react";

import { AUTO_REFRESH_RING_DIMENSIONS } from "@/constants/auto-refresh.constants";
import { cn } from "@/lib/utils";

interface IAutoRefreshRingProps {
  /** 1 (full ring, just reset) draining to 0 (about to refresh). */
  progress: number;
  isRefreshing: boolean;
  className?: string;
}

/**
 * A small circular countdown ring that drains smoothly via a CSS `stroke-dashoffset` transition
 * (tuned to the hook's tick rate, so it never jumps once-per-second), swapping to a spinner while
 * the refresh is actually in flight. Respects `prefers-reduced-motion`.
 */
export const AutoRefreshRing = ({ progress, isRefreshing, className }: IAutoRefreshRingProps) => {
  if (isRefreshing) {
    return (
      <Loader2 className={cn("h-4 w-4 animate-spin text-orange-500", className)} aria-hidden="true" />
    );
  }

  const { SIZE_PX: size, STROKE_WIDTH_PX: strokeWidth } = AUTO_REFRESH_RING_DIMENSIONS;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progress);
  const center = size / 2;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={cn("shrink-0 -rotate-90", className)}
      aria-hidden="true"
    >
      <circle cx={center} cy={center} r={radius} strokeWidth={strokeWidth} className="fill-none stroke-neutral-200" />
      <circle
        cx={center}
        cy={center}
        r={radius}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        // Duration matches `AUTO_REFRESH_DEFAULTS.TICK_MS` so the ring never visibly steps.
        className="fill-none stroke-orange-500 transition-[stroke-dashoffset] duration-200 ease-linear motion-reduce:transition-none"
      />
    </svg>
  );
};
